import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import {
  ConditionalTemplateQuestion_ConditionalTemplateQuestionDataGridPageFilter,
  ConditionalTemplateQuestion_FieldsFragment,
  ConditionalTemplateQuestion_GetConditionalTemplateQuestionsForConditionalTemplateQuestionDataGridDocument,
  ConditionalTemplateQuestionDataGridColumns,
  useConditionalTemplateQuestion_GetConditionalTemplateQuestionsForConditionalTemplateQuestionDataGridLazyQuery,
  useConditionalTemplateQuestion_UpdateConditionalTemplateQuestionMutation,
  useConditionalTemplateQuestionSet_GetConditionalTemplateQuestionSetByIdQuery,
} from 'queries';
import { ChevronRight } from 'ts-react-feather-icons';

import { ServerEnabledDataGrid } from 'common/server-enabled-data-grid';
import HomeLayout from 'layouts/home-layout';
import { GridStateKey } from 'utils/grid-state';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import { defaultPaginationModel } from 'utils/pagination-model';

import GridRowOrderAction from '../../common/grid-row-order-action';

import { ConditionalTemplateQuestionActions } from './components/conditional-template-question-actions';
import { CreateConditionalTemplateQuestionModal } from './components/create-conditional-template-question-modal';
import { EditConditionalTemplateQuestionModal } from './components/edit-conditional-template-question-modal';
import { useStyles } from './manage-conditional-template-questions-page.styles';

export const ManageConditionalTemplateQuestionsPage = () => {
  const classes = useStyles();
  const history = useHistory();

  const { conditionalTemplateQuestionSetId } = useParams<Record<string, string>>();

  const [selectedConditionalTemplateQuestion, setSelectedConditionalTemplateQuestion] = useState<
    ConditionalTemplateQuestion_FieldsFragment | undefined
  >(undefined);

  const [
    showCreateConditionalTemplateQuestionModal,
    setShowCreateConditionalTemplateQuestionModal,
  ] = useState<boolean>(false);

  const [showEditConditionalTemplateQuestionModal, setShowEditConditionalTemplateQuestionModal] =
    useState<boolean>(false);

  const [
    getConditionalTemplateQuestionsForConditionalTemplateQuestionDataGridLazyQuery,
    getConditionalTemplateQuestionsForConditionalTemplateQuestionDataGridLazyQueryResult,
  ] =
    useConditionalTemplateQuestion_GetConditionalTemplateQuestionsForConditionalTemplateQuestionDataGridLazyQuery(
      {
        variables: {
          input: {
            conditionalTemplateQuestionSetId: conditionalTemplateQuestionSetId,
            paginationInput: {
              page: defaultPaginationModel.page,
              pageSize: defaultPaginationModel.pageSize,
            },
          },
        },
        onError: (e) => {
          showAlert({
            message: 'Error getting conditional template questions: ' + e.message,
            severity: NotificationSeverity.ERROR,
          });
        },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
      },
    );

  const {
    data: conditionalTemplateQuestionSetData,
    loading: conditionalTemplateQuestionSetDataLoading,
  } = useConditionalTemplateQuestionSet_GetConditionalTemplateQuestionSetByIdQuery({
    fetchPolicy: 'network-only',
    variables: { id: conditionalTemplateQuestionSetId },
  });

  const [updateConditionalTemplateQuestion] =
    useConditionalTemplateQuestion_UpdateConditionalTemplateQuestionMutation({
      refetchQueries: [
        {
          query:
            ConditionalTemplateQuestion_GetConditionalTemplateQuestionsForConditionalTemplateQuestionDataGridDocument,
          variables: {
            input: {
              conditionalTemplateQuestionSetId: conditionalTemplateQuestionSetId,
              paginationInput: {
                page: defaultPaginationModel.page,
                pageSize: defaultPaginationModel.pageSize,
              },
            },
          },
        },
      ],
      onError: (e) => {
        showAlert({
          message: 'Error re-ordering conditional template questions: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const doesQuestionHaveFilterRelations = (
    conditionalTemplateQuestion: ConditionalTemplateQuestion_FieldsFragment,
  ): boolean => {
    return (conditionalTemplateQuestion.conditionalTemplateFilters?.length ?? 0) > 0;
  };

  const displayReorderConfirmation = (): boolean => {
    return window.confirm(
      'Re-ordering this question will affect associated question filters. Are you sure you want to proceed?',
    );
  };

  const handleOnRowOrderChange = async (
    conditionalTemplateQuestion: ConditionalTemplateQuestion_FieldsFragment,
    newOrder: number,
  ) => {
    // This is the question currently at the target index for the moving question
    const destinationConditionalTemplateQuestion =
      getConditionalTemplateQuestionsForConditionalTemplateQuestionDataGridLazyQueryResult.data
        ?.data?.edges?.[newOrder - 1]?.node as ConditionalTemplateQuestion_FieldsFragment;

    const hasFilterRelations =
      doesQuestionHaveFilterRelations(conditionalTemplateQuestion) ||
      doesQuestionHaveFilterRelations(destinationConditionalTemplateQuestion);

    const shouldReorder = hasFilterRelations ? displayReorderConfirmation() : true;

    if (!shouldReorder) {
      return;
    }

    updateConditionalTemplateQuestion({
      variables: {
        input: {
          id: conditionalTemplateQuestion.id,
          sortOrder: newOrder,
        },
      },
    });
  };

  const columns: GridColDef<ConditionalTemplateQuestion_FieldsFragment>[] = [
    {
      disableColumnMenu: true,
      field: ConditionalTemplateQuestionDataGridColumns.SortOrder,
      filterable: false,
      flex: 0.5,
      headerName: 'Order',
      renderCell: ({ row }) => {
        return (
          <GridRowOrderAction
            disableDown={
              row.sortOrder >=
              (getConditionalTemplateQuestionsForConditionalTemplateQuestionDataGridLazyQueryResult
                .data?.data?.edges?.length ?? 0)
            }
            disableUp={row.sortOrder === 1}
            handleDownClick={() => {
              handleOnRowOrderChange(row, row.sortOrder + 1);
            }}
            handleUpClick={() => {
              handleOnRowOrderChange(row, row.sortOrder - 1);
            }}
          />
        );
      },
    },
    {
      disableColumnMenu: true,
      field: ConditionalTemplateQuestionDataGridColumns.Question,
      filterable: false,
      flex: 2,
      headerName: 'Question',
      renderCell: ({ row }) => {
        return row.question;
      },
    },
    {
      disableColumnMenu: true,
      field: ConditionalTemplateQuestionDataGridColumns.QuestionAnswers,
      filterable: false,
      flex: 2,
      headerName: 'Answers',
      renderCell: ({ row }) => {
        return (
          <Button
            onClick={() =>
              history.push(
                `/home/${conditionalTemplateQuestionSetId}/${row.id}/manage-conditional-template-answers`,
              )
            }
          >
            Edit Answers ({row.conditionalTemplateAnswers?.length || 0})
          </Button>
        );
      },
    },
    {
      disableColumnMenu: true,
      field: ConditionalTemplateQuestionDataGridColumns.QuestionFilters,
      filterable: false,
      flex: 2,
      headerName: 'Filters',
      renderCell: ({ row }) => {
        return (
          <Button
            onClick={() =>
              history.push(
                `/home/${conditionalTemplateQuestionSetId}/${row.id}/manage-conditional-template-filters`,
              )
            }
          >
            Edit Filters ({row.conditionalTemplateFilters?.length || 0})
          </Button>
        );
      },
    },
    {
      disableColumnMenu: true,
      field: 'actions',
      filterable: false,
      flex: 0.5,
      headerName: 'Actions',
      renderCell: ({ row }) => {
        return (
          <ConditionalTemplateQuestionActions
            conditionalTemplateQuestion={row}
            onEditSelectedConditionalTemplateQuestion={() => {
              setSelectedConditionalTemplateQuestion(row);
              setShowEditConditionalTemplateQuestionModal(true);
            }}
          />
        );
      },
    },
  ];

  const isLoading =
    getConditionalTemplateQuestionsForConditionalTemplateQuestionDataGridLazyQueryResult.loading ||
    conditionalTemplateQuestionSetDataLoading;

  const isEditModalVisible =
    showEditConditionalTemplateQuestionModal && selectedConditionalTemplateQuestion;
  const isCreateModalVisible = showCreateConditionalTemplateQuestionModal;

  return (
    <HomeLayout showPortalName={false}>
      <>
        {isEditModalVisible && (
          <EditConditionalTemplateQuestionModal
            conditionalTemplateQuestionSetId={conditionalTemplateQuestionSetId}
            showModal={showEditConditionalTemplateQuestionModal}
            setShowModal={setShowEditConditionalTemplateQuestionModal}
            setSelectedConditionalTemplateQuestion={setSelectedConditionalTemplateQuestion}
            selectedConditionalTemplateQuestion={selectedConditionalTemplateQuestion}
          />
        )}

        {isCreateModalVisible && (
          <CreateConditionalTemplateQuestionModal
            conditionalTemplateQuestionSetId={conditionalTemplateQuestionSetId}
            showModal={showCreateConditionalTemplateQuestionModal}
            setShowModal={setShowCreateConditionalTemplateQuestionModal}
          />
        )}
        <Box className={classes.container}>
          <Box className={classes.actionBox}>
            <Typography className={classes.title} variant="h5">
              <Box
                component="span"
                onClick={() => history.push('/home')}
                className={classes.routeIconContainer}
              >
                <img src="/home-icon.svg" alt="home-icon" />
              </Box>

              <Box component="span" className={classes.routeIconContainer}>
                <ChevronRight />
              </Box>

              <Typography
                component="span"
                className={classes.link}
                onClick={() => history.push('/home/manage-conditional-template-question-sets')}
              >
                Conditional Question Sets
              </Typography>

              <Box component="span" className={classes.routeIconContainer}>
                <ChevronRight />
              </Box>

              <Typography component="span">Conditional Question</Typography>
            </Typography>
          </Box>

          <Typography variant="body1" className={classes.actionTypography}>
            {conditionalTemplateQuestionSetData?.data?.name || ''}
          </Typography>

          <Box className={classes.button}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              className={classes.actionButton}
              onClick={() => {
                setShowCreateConditionalTemplateQuestionModal(true);
              }}
            >
              + Create New Question
            </Button>
          </Box>

          <Box className={classes.flexBox}>
            <Box>
              <ServerEnabledDataGrid<ConditionalTemplateQuestion_ConditionalTemplateQuestionDataGridPageFilter>
                className={classes.root}
                loading={isLoading}
                queryResult={
                  getConditionalTemplateQuestionsForConditionalTemplateQuestionDataGridLazyQueryResult
                }
                executeLazyQuery={
                  getConditionalTemplateQuestionsForConditionalTemplateQuestionDataGridLazyQuery
                }
                gridStateKey={GridStateKey.ConditionalTemplateQuestionGrid}
                columns={columns}
                disableColumnReorder
                disableRowSelectionOnClick
                rowHeight={65}
                pagination
              />
            </Box>
          </Box>
        </Box>
      </>
    </HomeLayout>
  );
};
